import type { ReactNode } from 'react'

import { Card } from '@/shared/components/ui/card'
import { cn } from '@/shared/utils/cn'

export type ProvisioningStatus = 'provisioning' | 'blocked'

interface ProjectProvisioningStateProps {
  status: ProvisioningStatus
  blockReason?: string | null
  title?: string
  action?: ReactNode
  /** `card` — плитка в сетке проектов, `page` — отдельный экран вместо разделов проекта. */
  layout?: 'card' | 'page'
  className?: string
}

/**
 * Лист с тремя строками — то, чем занята сборка: читает страницы сайта. Пока идёт
 * работа, строки «дышат» по очереди; на остановленной сборке лист замирает.
 */
function SiteSheet({ status }: { status: ProvisioningStatus }) {
  const isBlocked = status === 'blocked'
  const lineWidths = ['w-full', 'w-4/5', 'w-3/5']

  return (
    <span
      aria-hidden="true"
      className={cn(
        'flex h-12 w-10 flex-none flex-col justify-center gap-1.5 rounded-md border p-2',
        isBlocked
          ? 'border-[var(--color-warning-border)] bg-[var(--color-warning-bg)]'
          : 'border-[var(--color-brand-border-subtle)] bg-[var(--color-brand-bg-subtle)]',
      )}
    >
      {lineWidths.map((width, index) => (
        <span
          className={cn(
            'h-1 rounded-full',
            width,
            isBlocked
              ? 'bg-[var(--color-warning-solid)] opacity-60 last:w-1/4'
              : 'provision-scan-line bg-[var(--color-brand)]',
          )}
          key={width}
          style={isBlocked ? undefined : { animationDelay: `${index * 0.25}s` }}
        />
      ))}
    </span>
  )
}

export function ProjectProvisioningState({
  status,
  blockReason,
  title,
  action,
  layout = 'card',
  className,
}: ProjectProvisioningStateProps) {
  const isBlocked = status === 'blocked'
  const isPage = layout === 'page'

  const heading = isBlocked ? 'Автосборка остановилась' : 'Готовим проект'
  const description = isBlocked
    ? `${blockReason ? `${blockReason}. ` : ''}Напишите в поддержку — поможем закончить настройку.`
    : 'Читаем страницы сайта и собираем базу знаний. Обычно занимает 5–15 минут, страница обновится сама.'

  return (
    <Card className={cn(isPage ? 'mx-auto w-full max-w-xl' : 'h-full', className)} padding="lg">
      <div className={cn('flex flex-col gap-4', !isPage && 'h-full')}>
        {title ? (
          <p className="truncate font-display text-base font-semibold text-[var(--color-text)]">
            {title}
          </p>
        ) : null}

        <div className="flex flex-1 items-start gap-3">
          <SiteSheet status={status} />
          <div className="min-w-0 space-y-1">
            <p className="font-display text-sm font-semibold text-[var(--color-text)]">{heading}</p>
            <p className="text-sm text-[var(--color-text-soft)]">{description}</p>
          </div>
        </div>

        {action ? <div className="flex flex-wrap items-center gap-2">{action}</div> : null}
      </div>
    </Card>
  )
}
